import React, { Fragment, useState } from "react";
import { Card, Button, Upload, Modal, message, notification } from "antd";
import { DownloadOutlined, DeleteOutlined, EyeOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { deleteData } from "apiServices/common";
import { useParams } from "react-router";

const { confirm } = Modal;

// Function to format status for human-readable display
const formatStatus = (status) => {
  if (!status) return "-";

  // Convert to string and handle different cases
  const statusStr = String(status).toLowerCase();

  // Replace underscores with spaces and capitalize each word
  return statusStr
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export default function UploadDocument({
  DOC_ADD = `/api/exim-cost/iou_document/store`,
  filesList,
  setFileList,
  view,
  record,
  getIOUCostInfo,
  iouId,
}) {
  // Debug: Log when filesList prop changes
  React.useEffect(() => {
    console.log("UploadDocument received filesList:", filesList);
  }, [filesList]);
  const [loading, setLoading] = useState(false);
  const { iouCostId } = useParams();

  const loggedInUserId = parseInt(localStorage.getItem("user_id"), 10);

  // Get IOU ID from record or prop
  const currentIouId = iouId || record?.id || record?.iou_id;

  // Check if user's role can approve or reject documents (to hide upload button)
  // Users with approve/reject permissions (Department Head, Director) should not see upload button
  const canUserApproveOrReject = React.useMemo(() => {
    if (!record?.user?.id) return true; // Allow upload if no record (create mode)
    if (loggedInUserId !== record?.user?.id) return false;
    return true;
  }, [record, loggedInUserId]);

  // const base_url = process.env.REACT_APP_BASE;
  const api_base_url = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("token");
  const props = {
    accept: "image/*",
    showUploadList: false,
    customRequest({ file, onSuccess, onError }) {
      if (!currentIouId) {
        notification.error({
          message: "Error",
          description: "Please select an IOU number first",
          placement: "topRight",
        });
        onError(new Error("IOU ID is required"));
        return;
      }

      setLoading(true);
      const formData = new FormData();
      formData.append("document", file);
      formData.append("iou_id", currentIouId);
      formData.append("document_name", file.name);

      fetch(api_base_url + DOC_ADD, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      })
        .then((res) => res.json())
        .then((response) => {
          onSuccess(response, file);
          setLoading(false);
        })
        .catch((err) => {
          onError(err);
          setLoading(false);
        });
    },
    async onChange(info) {
      if (info.file.status !== "uploading") {
      }
      if (info.file.status === "done" && info?.file?.response.code === 200) {
        let content = {
          id: info?.file?.response?.data?.id,
          name: info?.file?.response?.data?.document_name,
          path: info?.file?.response?.data?.document,
          status: info?.file?.response?.data?.status || "created",
        };

        setFileList((oldArray) => [...oldArray, content]);

        message.success(`Document uploaded successfully`);
      } else if (info.file.status === "done" && info?.file?.response.code !== 200) {
        message.error(`Document file upload failed.`);
      } else if (info.file.status === "error") {
        message.error(`Document file upload failed.`);
      }
    },
  };

  const removeFile = async (index, fileId) => {
    const file = filesList.find((doc) => doc.id === fileId);

    // Check if user is creator
    const isCreator = loggedInUserId === record?.user?.id;

    // Check if status is "created"
    const canDelete = file?.status === "created";

    if (!isCreator) {
      notification.warning({
        message: "Warning",
        description: "Only the creator can delete documents.",
        placement: "bottomRight",
      });
      return;
    }

    if (!canDelete) {
      notification.warning({
        message: "Warning",
        description: "Only documents with 'created' status can be deleted.",
        placement: "bottomRight",
      });
      return;
    }

    confirm({
      title: "Do you Want to delete this document?",
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        try {
          setLoading(true);
          const response = await deleteData(`/api/exim-cost/iou_document/delete/${fileId}`);

          if (response && (response?.code === 200 || response?.status === 200)) {
            const items = filesList.filter((doc) => doc.id !== fileId);
            setFileList(items);

            message.success("Document deleted successfully");

            // Refresh IOU cost info if callback is provided
            if (getIOUCostInfo && iouCostId) {
              getIOUCostInfo(iouCostId);
            }
          } else {
            notification.error({
              message: "Error",
              description: response?.message || "Failed to delete document",
              placement: "bottomRight",
            });
          }
        } catch (error) {
          notification.error({
            message: "Error",
            description: "Failed to delete document",
            placement: "bottomRight",
          });
        } finally {
          setLoading(false);
        }
      },
      onCancel() {},
    });
  };

  return (
    <Card className="mt-0 custome-table" title="IOU Document">
      <div className="root-table">
        <table>
          <thead>
            <tr>
              <th className="t-header bc-java">Serial</th>
              <th className="t-header bc-java">Document Name</th>
              <th className="t-header bc-java">File</th>
              <th className="t-header bc-java">Status</th>
              <th className="t-header bc-java">Comments</th>
              <th className="t-header bc-java"></th>
            </tr>
          </thead>
          <tbody>
            {filesList?.length ? (
              <>
                {filesList.map((file, index) => (
                  <Fragment key={index}>
                    <tr>
                      <td className="t-body">{index + 1}</td>
                      <td className="t-body">{file.name}</td>
                      <td className="t-body">
                        <Button type="text" onClick={() => window.open(api_base_url + "/" + file.path, "_blank")}>
                          <EyeOutlined />
                        </Button>
                      </td>
                      <td className="t-body">
                        {file.status ? (
                          <span
                            style={{
                              display: "inline-block",
                              padding: "2px 8px",
                              borderRadius: 4,
                              color: "#fff",
                              fontSize: 12,
                              textTransform: "capitalize",
                              backgroundColor:
                                file.status === "requested" || file.status === "requested_by_user"
                                  ? "#fa8c16" // orange
                                  : file.status === "created"
                                  ? "#1890ff" // blue
                                  : file.status === "rejected"
                                  ? "#ff4d4f" // red
                                  : file.status === "approved" || file.status === "approved_by_director"
                                  ? "#52c41a" // green
                                  : file.status === "by_department" || file.status === "approved_by_dept_head"
                                  ? "#13c2c2" // cyan
                                  : file.status === "by_director"
                                  ? "#722ed1" // purple
                                  : file.status === "by_account"
                                  ? "#eb2f96" // pink
                                  : "#595959", // default gray
                            }}
                          >
                            {formatStatus(file.status)}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="t-body">{file.comments || "-"}</td>
                      {!view && (
                        <td className="t-body">
                          {loggedInUserId === record?.user?.id && file.status === "created" ? (
                            <DeleteOutlined
                              style={{ fontSize: "20px", color: "red" }}
                              className="cursore-pointer"
                              onClick={() => removeFile(index, file.id)}
                            />
                          ) : null}
                        </td>
                      )}
                    </tr>
                  </Fragment>
                ))}
              </>
            ) : (
              ""
            )}

            <tr>
              <td className="t-body"></td>
              <td className="t-body"></td>
              <td className="t-body"></td>
              <td className="t-body">
                {/* {!view && canUserApproveOrReject && ( */}
                  <Upload {...props}>
                    <Button type="primary" icon={<DownloadOutlined />} loading={loading}>
                      Upload
                    </Button>
                  </Upload>
                {/* )} */}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
}
